import { useEffect, useMemo, useState } from 'react';
import { SEASONAL_ACCESSORIES } from './turboSeasons';
import { useCurrentInternalUser } from '../../../lib/currentUser';

// Decides which seasonal accessory (if any) Turbo should be wearing
// right now. Used by TurboIdle which renders the accessory's SVG
// fragment inside its own viewBox.
//
// Behaviour:
//   · Per-user opt-out via internal_users.seasonal_accessories_enabled.
//     Missing/undefined defaults to true so existing users see
//     accessories without explicit opt-in.
//   · Re-evaluates at midnight so an open dashboard rolls over to
//     the next day's accessory without a refresh.
//   · Returns the highest-priority active season when more than one
//     window matches (e.g. Easter beats Spring, anchored moments
//     beat vibe seasons).
//   · Returns null when the toggle is off, when no season is active,
//     or when called outside a logged-in internal context (the hook
//     gracefully no-ops if currentInternalUser hasn't loaded yet).
// `schedule` defaults to Turbo's SEASONAL_ACCESSORIES so existing Turbo
// callers are unchanged; Otto passes its own OTTO_SEASONS. The resolver
// logic (priorities, cross-year, Easter, opt-out, midnight roll-over) is
// shared — not rebuilt per mascot.
export default function useSeasonalAccessory(schedule = SEASONAL_ACCESSORIES) {
  const { user } = useCurrentInternalUser();
  const enabled = user?.seasonal_accessories_enabled ?? true;
  const [today, setToday] = useState(() => new Date());

  // Re-fire just after midnight so a long-open tab rolls over to the
  // next day's window automatically.
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 1, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();
    const timer = setTimeout(() => setToday(new Date()), msUntilMidnight);
    return () => clearTimeout(timer);
  }, [today]);

  return useMemo(() => {
    if (!enabled) return null;
    const active = schedule.filter(s => isActive(s, today));
    if (active.length === 0) return null;
    return active.sort((a, b) => b.priority - a.priority)[0];
  }, [enabled, today, schedule]);
}

// MMDD-string parser + range check. Crosses-year ranges (e.g. New
// Year, Dec 31 → Jan 10) are flagged when startMMDD > endMMDD and
// matched against either the leading or trailing portion.
function isActive(season, date) {
  if (season.computeDates) {
    const { start, end } = season.computeDates(date.getFullYear());
    return date >= start && date <= end;
  }
  if (!season.startMMDD || !season.endMMDD) return false;
  const [startMonth, startDay] = season.startMMDD.split('-').map(Number);
  const [endMonth, endDay] = season.endMMDD.split('-').map(Number);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const isCrossYear = startMonth > endMonth || (startMonth === endMonth && startDay > endDay);
  if (isCrossYear) {
    const inLeadingPortion = month > startMonth || (month === startMonth && day >= startDay);
    const inTrailingPortion = month < endMonth || (month === endMonth && day <= endDay);
    return inLeadingPortion || inTrailingPortion;
  }
  if (month < startMonth || month > endMonth) return false;
  if (month === startMonth && day < startDay) return false;
  if (month === endMonth && day > endDay) return false;
  return true;
}
